import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../context/AppContext';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  ArrowLeftIcon,
  PhotoIcon,
  DocumentIcon,
  PaperClipIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
  PrinterIcon,
  LinkIcon,
  TrashIcon,
  MapPinIcon,
  UserIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import PlotSearchModal from './PlotSearchModal';
import ContactSearchModal from './ContactSearchModal';
import DocumentGenerationPanel from './DocumentGenerationPanel';

const MAPBOX_DEFAULT_TOKEN = 'pk.eyJ1Ijoic2hhd25jcmFpbmUiLCJhIjoiY2xzN3Z5bmhwMjhzZTJrbnE5MGZ6NTI3YSJ9.K3ZOPNCbtyu6KYEtVO2jkQ';
mapboxgl.accessToken = (process.env.REACT_APP_MAPBOX_TOKEN && process.env.REACT_APP_MAPBOX_TOKEN.trim()) || MAPBOX_DEFAULT_TOKEN;

const WorkOrderDetail = ({ workOrderId, onBack, statuses, users }) => {
  const { token, user } = useContext(AppContext);
  const [workOrder, setWorkOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const fileInputRef = useRef(null);
  const [isPlotModalOpen, setIsPlotModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    fetchWorkOrder();
  }, [workOrderId]);

  // Initialize map when plots are available
  useEffect(() => {
    if (!workOrder?.plots?.length || !mapContainerRef.current) return;

    // Filter plots with valid geometry
    const plotsWithGeometry = workOrder.plots.filter(p => p.geometry);
    if (plotsWithGeometry.length === 0) return;

    // Clean up existing map
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    // Create GeoJSON from plots
    const geojson = {
      type: 'FeatureCollection',
      features: plotsWithGeometry.map(plot => ({
        type: 'Feature',
        properties: {
          fid: plot.fid,
          alias: plot.alias || `Plot ${plot.fid}`,
          section: plot.section || ''
        },
        geometry: plot.geometry
      }))
    };

    // Calculate bounds
    const bounds = new mapboxgl.LngLatBounds();
    geojson.features.forEach(feature => {
      const coords = feature.geometry.type === 'MultiPolygon'
        ? feature.geometry.coordinates.flat(2)
        : feature.geometry.coordinates.flat(1);
      coords.forEach(coord => bounds.extend(coord));
    });

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-v9',
      bounds: bounds,
      fitBoundsOptions: { padding: 50 },
      cooperativeGestures: true
    });

    mapRef.current = map;

    map.on('load', () => {
      // Add plot source
      map.addSource('work-order-plots', {
        type: 'geojson',
        data: geojson
      });

      // Add fill layer
      map.addLayer({
        id: 'plots-fill',
        type: 'fill',
        source: 'work-order-plots',
        paint: {
          'fill-color': '#22c55e',
          'fill-opacity': 0.5
        }
      });

      // Add outline layer
      map.addLayer({
        id: 'plots-outline',
        type: 'line',
        source: 'work-order-plots',
        paint: {
          'line-color': '#ffffff',
          'line-width': 2
        }
      });

      // Click handler for desktop
      let touchHandledClick = false;
      map.on('click', 'plots-fill', (e) => {
        if (touchHandledClick) {
          touchHandledClick = false;
          return;
        }
        const feature = e.features?.[0];
        if (!feature) return;
        showPlotPopup(feature, e.lngLat);
      });

      // Mobile tap detection
      let touchStartInfo = null;
      const canvas = map.getCanvas();

      canvas.addEventListener('touchstart', (e) => {
        if (e.touches.length === 1) {
          touchStartInfo = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
            time: Date.now()
          };
        } else {
          touchStartInfo = null;
        }
      }, { passive: true });

      canvas.addEventListener('touchend', (e) => {
        if (!touchStartInfo) return;
        const touch = e.changedTouches[0];
        const dx = touch.clientX - touchStartInfo.x;
        const dy = touch.clientY - touchStartInfo.y;
        const dt = Date.now() - touchStartInfo.time;
        touchStartInfo = null;

        // Detect tap: minimal movement (<10px), short duration (<300ms)
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && dt < 300) {
          const rect = canvas.getBoundingClientRect();
          const point = [touch.clientX - rect.left, touch.clientY - rect.top];
          const features = map.queryRenderedFeatures(point, { layers: ['plots-fill'] });
          if (features.length > 0) {
            touchHandledClick = true;
            const lngLat = map.unproject(point);
            showPlotPopup(features[0], lngLat);
          }
        }
      }, { passive: true });

      // Change cursor on hover
      map.on('mouseenter', 'plots-fill', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'plots-fill', () => {
        map.getCanvas().style.cursor = '';
      });
    });

    const showPlotPopup = (feature, lngLat) => {
      if (popupRef.current) popupRef.current.remove();

      const { alias, section } = feature.properties;
      const html = `
        <div class="p-2">
          <p class="font-semibold text-gray-900">${alias}</p>
          ${section ? `<p class="text-sm text-gray-600">Section: ${section}</p>` : ''}
        </div>
      `;

      popupRef.current = new mapboxgl.Popup({ closeButton: true, maxWidth: '200px' })
        .setLngLat(lngLat)
        .setHTML(html)
        .addTo(map);
    };

    return () => {
      if (popupRef.current) popupRef.current.remove();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [workOrder?.plots]);

  const fetchWorkOrder = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/work-orders/${workOrderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkOrder(data);
        setEditData({
          title: data.title,
          description: data.description,
          workOrderType: data.workOrderType,
          priority: data.priority,
          statusId: data.statusId,
          assignedTo: data.assignedTo || '',
          dueDate: data.dueDate || '',
          includeMap: data.includeMap,
          notifyOnStatusChange: data.notifyOnStatusChange
        });
      }
    } catch (error) { console.error('Error:', error); }
    finally { setIsLoading(false); }
  };

  const handleStatusChange = async (statusId) => {
    setIsSaving(true);
    try {
      await fetch(`/api/work-orders/${workOrderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ statusId: parseInt(statusId) })
      });
      fetchWorkOrder();
    } catch (error) { console.error('Error:', error); }
    finally { setIsSaving(false); }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await fetch(`/api/work-orders/${workOrderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...editData,
          assignedTo: editData.assignedTo ? parseInt(editData.assignedTo) : null
        })
      });
      setEditing(false);
      fetchWorkOrder();
    } catch (error) { console.error('Error:', error); }
    finally { setIsSaving(false); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setIsSaving(true);
    try {
      await fetch(`/api/work-orders/${workOrderId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: newNote })
      });
      setNewNote('');
      fetchWorkOrder();
    } catch (error) { console.error('Error:', error); }
    finally { setIsSaving(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        await fetch(`/api/work-orders/${workOrderId}/attachments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            attachmentType: file.type.startsWith('image/') ? 'image' : 'document',
            title: file.name,
            fileName: file.name,
            fileType: file.type,
            fileData: event.target.result,
            fileSize: file.size
          })
        });
        fetchWorkOrder();
      } catch (error) { console.error('Error:', error); }
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await fetch(`/api/work-orders/${workOrderId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWorkOrder();
    } catch (error) { console.error('Error:', error); }
  };

  const handleAddPlot = async (plot) => {
    setIsPlotModalOpen(false);
    const existingPlotIds = (workOrder.plots || []).map(p => p.fid);
    if (existingPlotIds.includes(plot.fid)) return;
    try {
      await fetch(`/api/work-orders/${workOrderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plotIds: [...existingPlotIds, plot.fid] })
      });
      fetchWorkOrder();
    } catch (error) { console.error('Error:', error); }
  };

  const handleRemovePlot = async (plotFid) => {
    const remainingPlotIds = (workOrder.plots || []).filter(p => p.fid !== plotFid).map(p => p.fid);
    try {
      await fetch(`/api/work-orders/${workOrderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plotIds: remainingPlotIds })
      });
      fetchWorkOrder();
    } catch (error) { console.error('Error:', error); }
  };

  const handleAddContact = async (contact) => {
    setIsContactModalOpen(false);
    const existingContactIds = (workOrder.contacts || []).map(c => c.id);
    if (existingContactIds.includes(contact.id)) return;
    try {
      await fetch(`/api/work-orders/${workOrderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contactIds: [...existingContactIds, contact.id] })
      });
      fetchWorkOrder();
    } catch (error) { console.error('Error:', error); }
  };

  const handleRemoveContact = async (contactId) => {
    const remainingContactIds = (workOrder.contacts || []).filter(c => c.id !== contactId).map(c => c.id);
    try {
      await fetch(`/api/work-orders/${workOrderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contactIds: remainingContactIds })
      });
      fetchWorkOrder();
    } catch (error) { console.error('Error:', error); }
  };

  const handleViewAttachment = async (att) => {
    try {
      const response = await fetch(`/api/work-orders/${workOrderId}/attachments/${att.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.fileData) {
          const win = window.open('', '_blank');
          if (att.attachmentType === 'image') {
            win.document.write(`<html><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#111"><img src="${data.fileData}" style="max-width:100%;max-height:100vh;object-fit:contain" /></body></html>`);
          } else {
            // For documents, try to create a download link
            const link = document.createElement('a');
            link.href = data.fileData;
            link.download = att.fileName || att.title || 'attachment';
            link.click();
            win.close();
          }
        }
      }
    } catch (error) { console.error('Error viewing attachment:', error); }
  };

  const copyPublicLink = () => {
    if (workOrder?.statusUpdateToken) {
      const link = `${window.location.origin}/work-orders/update/${workOrder.statusUpdateToken}`;
      navigator.clipboard.writeText(link);
      alert('Public update link copied to clipboard');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin h-8 w-8 border-2 border-brand-green-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  if (!workOrder) {
    return <div className="p-6 text-center text-gray-500">Work order not found</div>;
  }

  const typeLabels = {
    funeral_preparation: 'Funeral Preparation',
    headstone_installation: 'Headstone Installation',
    repair: 'Repair', maintenance: 'Maintenance',
    landscaping: 'Landscaping', other: 'Other'
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-2 rounded-md hover:bg-gray-100">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex-1">
          {editing ? (
            <input
              type="text"
              value={editData.title}
              onChange={e => setEditData(prev => ({ ...prev, title: e.target.value }))}
              className="text-xl font-bold w-full px-2 py-1 border rounded-md"
            />
          ) : (
            <h1 className="text-xl font-bold">{workOrder.title}</h1>
          )}
          <p className="text-sm text-gray-500">#{workOrder.id} - Created {new Date(workOrder.createdAt).toLocaleDateString()}</p>
        </div>
        <div className="flex gap-2">
          {user.role !== 'grounds_crew' && (
            <>
              <button onClick={copyPublicLink} className="p-2 text-gray-500 hover:text-brand-green-600 rounded-md hover:bg-gray-100" title="Copy public link">
                <LinkIcon className="h-5 w-5" />
              </button>
              <button onClick={handlePrint} className="p-2 text-gray-500 hover:text-brand-green-600 rounded-md hover:bg-gray-100" title="Print">
                <PrinterIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Main content */}
        <div className="md:col-span-2 space-y-4">
          {/* Description */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium text-gray-900 mb-2">Description</h3>
            {editing ? (
              <textarea
                value={editData.description || ''}
                onChange={e => setEditData(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2 border rounded-md"
              />
            ) : (
              <p className="text-gray-700 whitespace-pre-wrap">{workOrder.description || 'No description'}</p>
            )}
          </div>

          {/* Plot Info */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <MapPinIcon className="h-4 w-4" />
                Associated Plots ({workOrder.plots?.length || 0})
              </h3>
              {user.role !== 'grounds_crew' && (
                <button onClick={() => setIsPlotModalOpen(true)} className="text-xs text-brand-green-600 hover:text-brand-green-700 flex items-center gap-0.5">
                  <PlusIcon className="h-3 w-3" /> Add
                </button>
              )}
            </div>
            {workOrder.plots && workOrder.plots.length > 0 ? (
              <>
                <div className="space-y-1 mb-3">
                  {workOrder.plots.map(plot => (
                    <div key={plot.fid} className="flex items-center justify-between group">
                      <p className="text-sm text-gray-700">
                        {plot.alias || `Plot ${plot.fid}`}
                        {plot.section && ` - ${plot.section}`}
                      </p>
                      {user.role !== 'grounds_crew' && (
                        <button onClick={() => handleRemovePlot(plot.fid)} className="text-red-400 opacity-0 group-hover:opacity-100 p-0.5">
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {/* Map Controls Hint - Mobile only */}
                <div className="md:hidden bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-sm text-blue-700">
                  <p className="font-medium mb-1">Map Controls:</p>
                  <ul className="text-xs space-y-0.5">
                    <li>&bull; <strong>Pinch</strong> to zoom in/out</li>
                    <li>&bull; <strong>Two fingers</strong> to pan the map</li>
                    <li>&bull; <strong>One finger</strong> scrolls the page</li>
                    <li>&bull; <strong>Tap</strong> a plot to view details</li>
                  </ul>
                </div>
                {/* Plot Map */}
                <div
                  ref={mapContainerRef}
                  className="w-full h-64 rounded-lg overflow-hidden"
                  style={{ touchAction: 'pan-y' }}
                />
              </>
            ) : (
              <p className="text-xs text-gray-400">No plots associated</p>
            )}
          </div>

          {/* Attachments */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <PaperClipIcon className="h-4 w-4" />
                Attachments ({workOrder.attachments?.length || 0})
              </h3>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-brand-green-600 hover:text-brand-green-700"
                >
                  + Add
                </button>
              </div>
            </div>
            {workOrder.attachments?.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {workOrder.attachments.map(att => (
                  <div key={att.id} className="relative group border rounded-md p-2 flex items-center gap-2 cursor-pointer hover:bg-gray-50" onClick={() => handleViewAttachment(att)}>
                    {att.attachmentType === 'image' ? (
                      <PhotoIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    ) : (
                      <DocumentIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    )}
                    <span className="text-xs truncate">{att.title || att.fileName}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteAttachment(att.id); }}
                      className="absolute top-1 right-1 p-0.5 text-red-400 opacity-0 group-hover:opacity-100"
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No attachments</p>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <ChatBubbleLeftIcon className="h-4 w-4" />
              Notes
            </h3>
            <div className="space-y-3 mb-3">
              {workOrder.notes?.length > 0 ? (
                workOrder.notes.map(note => (
                  <div key={note.id} className="bg-gray-50 rounded-md p-3">
                    <p className="text-sm text-gray-700">{note.note}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {note.createdByName} - {new Date(note.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400">No notes yet</p>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddNote(); }}
                placeholder="Add a note..."
                className="flex-1 px-3 py-2 border rounded-md text-sm"
              />
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim() || isSaving}
                className="px-3 py-2 bg-brand-green-600 text-white rounded-md text-sm hover:bg-brand-green-700 disabled:bg-gray-300"
              >
                Add
              </button>
            </div>
          </div>

          {/* Status History */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <ClockIcon className="h-4 w-4" />
              Status History
            </h3>
            <div className="space-y-2">
              {(workOrder.statusHistory || []).map((entry, idx) => (
                <div key={idx} className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-brand-green-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{entry.statusName}</span>
                  <span className="text-gray-400">by {entry.changedBy}</span>
                  <span className="text-gray-400 ml-auto">{new Date(entry.date).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-lg shadow p-4">
            <DocumentGenerationPanel
              entityType="work_order"
              entityId={workOrder.id}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Status</h3>
            <select
              value={workOrder.statusId || ''}
              onChange={e => handleStatusChange(e.target.value)}
              disabled={isSaving}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              {statuses.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Details */}
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            {editing ? (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-500">Type</label>
                  <select
                    value={editData.workOrderType}
                    onChange={e => setEditData(prev => ({ ...prev, workOrderType: e.target.value }))}
                    className="w-full mt-1 px-2 py-1.5 border rounded-md text-sm"
                  >
                    {Object.entries(typeLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Priority</label>
                  <select
                    value={editData.priority}
                    onChange={e => setEditData(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full mt-1 px-2 py-1.5 border rounded-md text-sm"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Assigned To</label>
                  <select
                    value={editData.assignedTo}
                    onChange={e => setEditData(prev => ({ ...prev, assignedTo: e.target.value }))}
                    className="w-full mt-1 px-2 py-1.5 border rounded-md text-sm"
                  >
                    <option value="">Unassigned</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Due Date</label>
                  <input
                    type="date"
                    value={editData.dueDate}
                    onChange={e => setEditData(prev => ({ ...prev, dueDate: e.target.value }))}
                    className="w-full mt-1 px-2 py-1.5 border rounded-md text-sm"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button onClick={handleSave} disabled={isSaving} className="flex-1 py-1.5 text-sm bg-brand-green-600 text-white rounded-md">
                    Save
                  </button>
                  <button onClick={() => setEditing(false)} className="flex-1 py-1.5 text-sm bg-gray-200 rounded-md">
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <span className="text-xs font-medium text-gray-500 block">Type</span>
                  <span className="text-sm">{typeLabels[workOrder.workOrderType]}</span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 block">Priority</span>
                  <span className="text-sm capitalize">{workOrder.priority}</span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 block">Assigned To</span>
                  <span className="text-sm">
                    {workOrder.assignee ? `${workOrder.assignee.firstName} ${workOrder.assignee.lastName}` : 'Unassigned'}
                  </span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 block">Due Date</span>
                  <span className="text-sm">{workOrder.dueDate ? new Date(workOrder.dueDate).toLocaleDateString() : 'None'}</span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 block">Created By</span>
                  <span className="text-sm">
                    {workOrder.creator ? `${workOrder.creator.firstName} ${workOrder.creator.lastName}` : 'Unknown'}
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">Contacts</span>
                    {user.role !== 'grounds_crew' && (
                      <button onClick={() => setIsContactModalOpen(true)} className="text-xs text-brand-green-600 hover:text-brand-green-700 flex items-center gap-0.5">
                        <PlusIcon className="h-3 w-3" /> Add
                      </button>
                    )}
                  </div>
                  {workOrder.contacts && workOrder.contacts.length > 0 ? (
                    workOrder.contacts.map(c => (
                      <div key={c.id} className="flex items-center justify-between group">
                        <span className="text-sm">{c.firstName} {c.lastName}</span>
                        {user.role !== 'grounds_crew' && (
                          <button onClick={() => handleRemoveContact(c.id)} className="text-red-400 opacity-0 group-hover:opacity-100 p-0.5">
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400">No contacts</p>
                  )}
                </div>
                {user.role !== 'grounds_crew' && (
                  <button
                    onClick={() => setEditing(true)}
                    className="w-full mt-2 py-1.5 text-sm text-brand-green-600 border border-brand-green-600 rounded-md hover:bg-brand-green-50"
                  >
                    Edit Details
                  </button>
                )}
              </>
            )}
          </div>

          {/* Public Link */}
          {user.role !== 'grounds_crew' && workOrder.statusUpdateToken && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Quick Update Link</h3>
              <p className="text-xs text-gray-500 mb-2">Share this link with recipients to update status without logging in.</p>
              <button
                onClick={copyPublicLink}
                className="w-full py-1.5 text-sm text-brand-green-600 border border-brand-green-600 rounded-md hover:bg-brand-green-50"
              >
                Copy Link
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search Modals */}
      <PlotSearchModal
        open={isPlotModalOpen}
        onSelect={handleAddPlot}
        onClose={() => setIsPlotModalOpen(false)}
        excludeIds={(workOrder.plots || []).map(p => p.fid)}
      />
      <ContactSearchModal
        open={isContactModalOpen}
        onSelect={handleAddContact}
        onClose={() => setIsContactModalOpen(false)}
        excludeIds={(workOrder.contacts || []).map(c => c.id)}
      />
    </div>
  );
};

export default WorkOrderDetail;
